<script setup>
import { watch, onMounted, ref, inject, computed } from 'vue'
import { useEventListener } from '@vueuse/core'
import { useActionCenterStore } from '../../stores/ActionCenterStore'
import { storeToRefs } from 'pinia'
import FilterDropdown from '../common/FilterDropdown.vue'
import FilterInput from '../common/FilterInput.vue'
import BcsDynamicTextVue from '../BcsDynamicText.vue'

const actionCenterStore = useActionCenterStore()
const emitter = inject('emitter')

// Track if initial mount is complete
const isInitialLoadComplete = ref(false)

// Inline error state for Full-Time Employees input
const ftEmployeesError = ref('')

// Track sticky/scrolled state with hysteresis to prevent infinite loop
const isScrolled = ref(false)
const SCROLL_THRESHOLD_DOWN = 70
const SCROLL_THRESHOLD_UP = 1

const handleScroll = () => {
  const scrollY = window.scrollY
  if (!isScrolled.value && scrollY > SCROLL_THRESHOLD_DOWN) {
    isScrolled.value = true
  } else if (isScrolled.value && scrollY < SCROLL_THRESHOLD_UP) {
    isScrolled.value = false
  }
}

// Get state from store
const {
  accountOwners,
  planYearStartDates,
  erisaPlans,
  medicalPlans,
  coveragesOptions,
  selectedFilters,
  isLoadingPlanYears
} = storeToRefs(actionCenterStore)

// Computed: Build grouped options for Plan Year Start Date filter (array format)
const groupedPlanYearOptions = computed(() => {
  const groups = actionCenterStore.getGroupedPlanYearStartDates()
  
  return [
    { label: 'Active', options: groups.active },
    { label: 'Inactive', options: groups.inactive }
  ]
})

/**
 * Normalizes the multi-select Account Owner selection:
 *  - empty selection reverts to ['all']
 *  - picking 'all' clears any specific selection (and vice versa)
 *  - 'unassigned' OR-combines freely with specific owners
 */
function normalizeOwnerSelection(prev, next) {
  const previous = Array.isArray(prev) ? prev : []
  const incoming = Array.isArray(next) ? next : []
  if (incoming.length === 0) return ['all']
  if (incoming.includes('all') && !previous.includes('all')) return ['all']
  if (incoming.includes('all') && incoming.length > 1) return incoming.filter(v => v !== 'all')
  return incoming
}

function onAccountOwnerSelection(next) {
  selectedFilters.value.accountOwner = normalizeOwnerSelection(selectedFilters.value.accountOwner, next)
}

const sameSelection = (a, b) => {
  const listA = Array.isArray(a) ? [...a].sort() : []
  const listB = Array.isArray(b) ? [...b].sort() : []
  return listA.join('|') === listB.join('|')
}

// Watch for changes in Account Owner to reload Plan Years
watch(() => selectedFilters.value.accountOwner, async (newSelection, oldSelection) => {
  if (!newSelection || newSelection.length === 0) return
  if (!isInitialLoadComplete.value) return
  // Normalization creates new arrays; skip reloads when the set didn't actually change
  if (sameSelection(newSelection, oldSelection)) return

  emitter.emit('toggleLoading', { value: true })

  try {
    // Clear Plan Year Start Date selection since options depend on Account Owner
    selectedFilters.value.planYearStartDate = []

    // Hide tables when account owner changes so user must reload
    emitter.emit('action-center:hide-tables')

    const needsFresh = await actionCenterStore.shouldReloadData()
    await actionCenterStore.loadPlanYearsByAccountOwner(newSelection, { forceReload: needsFresh })
  } finally {
    emitter.emit('toggleLoading', { value: false })
  }
})

// Watch the unified filters object (except accountOwner which has its own watcher)
watch(
  () => ({
    planYearStartDate: selectedFilters.value.planYearStartDate,
    erisaPlan: selectedFilters.value.erisaPlan,
    medicalPlan: selectedFilters.value.medicalPlan,
    coverages: selectedFilters.value.coverages,
    fullTimeEmployees: selectedFilters.value.fullTimeEmployees
  }),
  () => {
    if (isInitialLoadComplete.value) {
      actionCenterStore.applyFilters()
    }
  },
  { deep: true }
)

// Watch for account owner counts loaded async (via loadAccountOwnerPlanYearCounts).
// If the logged-in user was selected by default but has 0 plan years, switch to 'all'.
const hasCheckedInitialCounts = ref(false)
watch(accountOwners, (owners) => {
  if (hasCheckedInitialCounts.value) return
  if (!isInitialLoadComplete.value) return

  const currentEmail = actionCenterStore.getCurrentUserEmail()
  if (!currentEmail) return
  const selection = selectedFilters.value.accountOwner || []
  if (selection.length !== 1 || selection[0] !== currentEmail) return

  const currentOwner = owners.find(o => o.value === currentEmail)
  if (!currentOwner || currentOwner.count === null) return

  hasCheckedInitialCounts.value = true

  if (currentOwner.count === 0) {
    selectedFilters.value.accountOwner = ['all']
  }
}, { deep: true })

// Reset filters
const resetFilters = () => {
  actionCenterStore.resetFilters()
}

onMounted(async () => {
  // Add scroll listener for sticky compact mode
  useEventListener(window, 'scroll', handleScroll)
  handleScroll() // Check initial scroll position

  // Mark as loading before the first await so ActionCenter.vue onMounted (which runs
  // after this yield) sees isLoadingPlanYears = true and defers scroll restore to the watcher
  isLoadingPlanYears.value = true

  // Check if we need to reload data from the backend
  const needsReload = await actionCenterStore.shouldReloadData()
  if (needsReload) {
    emitter.emit('toggleLoading', { value: true })

    // Load all filters from store (loads options but not plan years)
    await actionCenterStore.loadAllFilters()
    
    // Ensure we have a selected account owner
    if ((selectedFilters.value.accountOwner || []).length === 0 && accountOwners.value.length > 0) {
      selectedFilters.value.accountOwner = actionCenterStore.getDefaultAccountOwner()
    }

    // Load fresh plan years for the current account owner selection
    if ((selectedFilters.value.accountOwner || []).length > 0) {
      await actionCenterStore.loadPlanYearsByAccountOwner(selectedFilters.value.accountOwner, { forceReload: true })
    }

    emitter.emit('toggleLoading', { value: false })
  } else {
    // Data is cached and fresh - no backend calls needed
    isLoadingPlanYears.value = false
  }

  isInitialLoadComplete.value = true
})

// Normalize and format ranges for Full-Time Employees on blur/change
function onFullTimeEmployeesBlur(val) {
  const raw = (val ?? '').toString().trim()
  if (raw === '') {
    ftEmployeesError.value = ''
    selectedFilters.value.fullTimeEmployees = ''
    return
  }

  // Replace en/em dashes with hyphen, normalize separators
  const normalized = raw
    .replace(/[–—]/g, '-')
    .replace(/\bto\b/gi, '-')
    .replace(/\s+/g, ' ')
    .trim()

  const defaultError = 'Enter as range, e.g. 1-35'
  // Extract first two numbers
  const nums = normalized.match(/\d+/g)
  if (!nums || nums.length < 2) {
    ftEmployeesError.value = defaultError
    return
  }

  let a = parseInt(nums[0], 10)
  let b = parseInt(nums[1], 10)
  if (isNaN(a) || isNaN(b)) {
    ftEmployeesError.value = defaultError
    return
  }
  if (a > b) [a, b] = [b, a]

  // Update model with formatted value "a - b"
  selectedFilters.value.fullTimeEmployees = `${a} - ${b}`
  ftEmployeesError.value = ''
}
</script>

<template>
  <div class="d-flex parent-container" :class="{ 'is-scrolled': isScrolled }">
    <div class="w-100">
      <!-- Header -->
      <div class="header-container bcs-page-padding">
        <h1 class="header-28 text-secondary mb-0" id="actionCenterTitle">
          <med>Book Overview & Action Center</med>
        </h1>
        <p class="text-16 mb-0 mt-2.5" id="actionCenterHeadline">
          <BcsDynamicTextVue text="actionCenterHeadline" />
        </p>
      </div>
      
      <!-- Filters -->
      <div class="filters-container">
        <div class="filters-content">
          <div class="d-flex gap-3 align-items-end flex-wrap bcs-page-padding">
            <!-- Account Owner Filter -->
            <div class="filter-item">
              <FilterDropdown
                id="accountOwnerFilter"
                label="Account Owner"
                :model-value="selectedFilters.accountOwner"
                @update:model-value="onAccountOwnerSelection"
                :options="accountOwners"
                :multi-select="true"
                :has-default-selection="true"
              />
            </div>

            <!-- Plan Year Start Date Filter -->
            <div class="filter-item">
              <FilterDropdown
                id="planYearFilter"
                label="Plan Year Start Date"
                v-model="selectedFilters.planYearStartDate"
                :grouped-options="groupedPlanYearOptions"
                :multi-select="true"
                tooltip-text="Filter by plan year start dates. Active dates are plan years currently in effect."
              />
            </div>

            <!-- ERISA Plan Filter -->
            <div class="filter-item">
              <FilterDropdown
                id="erisaPlanFilter"
                label="ERISA Plan"
                v-model="selectedFilters.erisaPlan"
                :options="erisaPlans"
                :multi-select="true"
              />
            </div>

            <!-- Full-Time Employees Filter -->
            <div class="filter-item">
              <FilterInput
                id="fullTimeEmployeesFilter"
                label="Full-Time Employees"
                v-model="selectedFilters.fullTimeEmployees"
                placeholder="Eg., 1-35"
                type="text"
                :error-message="ftEmployeesError"
                @blur="onFullTimeEmployeesBlur"
                tooltipText="Enter a range of full time employers to filter for here - ex., 1-25, 100-999, etc."
              />
            </div>

            <!-- Medical Plan Filter -->
            <div class="filter-item">
              <FilterDropdown
                id="medicalPlanFilter"
                label="Medical Funding Type"
                v-model="selectedFilters.medicalPlan"
                :options="medicalPlans"
                :multi-select="true"
              />
            </div>

            <!-- Coverages Filter -->
            <div class="filter-item">
              <FilterDropdown
                id="coveragesFilter"
                label="Coverages"
                v-model="selectedFilters.coverages"
                :options="coveragesOptions"
                :multi-select="true"
              />
            </div>

            <!-- Reset Filters Button -->
            <div class="ps-2.5 button-container text-nowrap">
              <button
                id="resetFiltersBtn"
                @click="resetFilters"
                class="btn btn-link text-decoration-none text-primary p-0 reset-button text-16"
              >
              <med>
                <i class="bi bi-arrow-repeat me-1"></i>
                <span class="">Reset Filters</span>
              </med>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.header-container {
  background: #fff;
  padding-top: 1.85rem;
  padding-bottom: 1.85rem;
  transition: padding 0.2s ease;
}

.filters-container {
  background: #fff;
  border-bottom: 1px solid #7f8080a6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 0;
  padding-bottom: 1.8rem;
  transition: padding 0.2s ease;
}

.filters-content {
  padding: 0rem 0 1rem 0;
  transition: padding 0.2s ease;
}

.filter-item {
  flex: 1 1 0;
  min-width: 160px;

  :deep(.form-control),
  :deep(.btn) {
    transition: height 0.2s ease, padding 0.2s ease, min-height 0.2s ease;
  }

  :deep(label) {
    margin-bottom: 0.6rem !important;
    transition: margin 0.2s ease;
  }
}

.btn-link {
  font-weight: 400;
  
  &:hover {
    text-decoration: underline !important;
  }
  
  &:focus {
    box-shadow: none;
  }
  
  i {
    font-size: 1rem;
  }
}

.button-container {
  flex: 1 1 0;
  max-width: 140px;
  min-width: 110px;
}

.reset-button {
  padding-bottom: 0.7rem !important;
}

.parent-container {
  position: sticky;
  top: 61px;
  z-index: 1021;

  // Compact mode when scrolled
  &.is-scrolled {
    .header-container {
      padding-top: 1.5rem;
      padding-bottom: 0rem;
    }

    .filters-container {
      padding-bottom: 1.375rem;
    }

    .filters-content {
      padding: 0.6rem 0 0.5rem 0;
    }

    .filter-item {
      :deep(.form-control),
      :deep(.btn) {
        height: 28px;
        min-height: 28px;
        padding-top: 0.125rem;
        padding-bottom: 0.125rem;
      }

      :deep(label) {
        margin-bottom: 0.375rem !important;
      }
    }

    .reset-button {
      padding-bottom: 0.1rem !important;
    }
  }
}
</style>
